if (!customElements.get('product-purchase')) {
  customElements.define(
    'product-purchase',
    class ProductPurchase extends HTMLElement {
      connectedCallback() {
        if (this.dataset.initialized === 'true') return;

        this.form = this.querySelector('[data-bundle-form]');
        this.mediaItems = [...this.querySelectorAll('[data-media-item]')];
        this.mediaDots = [...this.querySelectorAll('[data-gallery-dot]')];
        this.modeButtons = [...this.querySelectorAll('[data-gallery-mode]')];
        this.tabButtons = [...this.querySelectorAll('[data-config-tab]')];
        this.panels = [...this.querySelectorAll('[data-config-panel]')];
        this.recommendations = [...this.querySelectorAll('[data-recommendation]')];
        this.recSolarOptions = [...this.querySelectorAll('[data-rec-solar]')];
        this.customSolarOptions = [...this.querySelectorAll('[data-custom-solar]')];
        this.accessories = [...this.querySelectorAll('[data-accessory]')];
        this.recBattery = this.querySelector('[data-rec-battery]');
        this.mainProduct = this.querySelector('[data-bundle-main]');
        this.battery = this.querySelector('[data-bundle-battery]');
        this.gifts = [...this.querySelectorAll('[data-gift]')];
        // Per-card gift state: { selected: boolean, dismissed: boolean }
        // dismissed = user manually unchecked this gift during a custom
        // session; it must never be auto-checked again until the user clicks
        // the card again (or the mode is reset).
        this.giftState = new Map();
        this.activeGifts = [];
        this.productCard = this.querySelector('[data-product-card]');
        this.stickyBar = this.querySelector('[data-sticky-bar]');
        this.addButtons = [...this.querySelectorAll('[data-add-to-cart]')];
        this.buyButtons = [...this.querySelectorAll('[data-buy-now]')];
        this.addButton = this.addButtons[0];
        this.buyButton = this.buyButtons[0];
        this.taxModal = this.querySelector('[data-tax-modal]');
        this.taxCheckbox = this.querySelector('[data-tax-checkbox]');
        this.galleryModal = this.querySelector('[data-gallery-modal]');
        this.galleryModalImage = this.querySelector('[data-gallery-modal-image]');
        this.galleryModalThumbs = this.querySelector('[data-gallery-modal-thumbs]');
        this.galleryDiscount = this.querySelector('[data-gallery-discount]');
        this.mode = 'recommended';
        this.batteryQuantity = 0;
        this.mainQuantity = 1;
        this.accessoryQuantities = new Map();
        this.pendingAction = null;
        this.taxConfirmed = false;
        this.activeGroup = 'product';
        this.galleryMaps = this.parseGalleryMaps();
        this.i18n = this.parseI18n();

        this.bindGallery();
        this.bindGallerySticky();
        this.bindStickyVisibility();
        this.bindStickyToggle();
        this.bindTabs();
        this.bindConfiguration();
        this.bindPurchase();
        this.bindTaxModal();
        this.bindPromoCountdowns();
        this.restoreUrlState();
        this.updateSummary();
        this.dataset.initialized = 'true';
      }

      disconnectedCallback() {
        this.productCardObserver?.disconnect();
        document.removeEventListener('click', this.stickyOutsideHandler);
        this.removeEventListener('keydown', this.stickyEscapeHandler);
        window.removeEventListener('resize', this.galleryStickyHandler);
        if (this._promoCdTimer) {
          clearInterval(this._promoCdTimer);
          this._promoCdTimer = null;
        }
      }

      /* ── Promo banner countdown ──
         data-end-epoch is resolved server-side by Liquid in the store's time
         zone and takes priority; data-end is the local-parse fallback. All
         instances share one setInterval; only changed text is written. */
      bindPromoCountdowns() {
        this.promoCountdowns = [...this.querySelectorAll('[data-promo-countdown]')];
        if (!this.promoCountdowns.length) return;

        this.promoCountdowns.forEach((el) => {
          el._fpTarget = 0;
          el._fpDone = false;
          const epoch = Number(el.dataset.endEpoch);
          if (Number.isFinite(epoch) && epoch > 0) {
            el._fpTarget = epoch * 1000;
            return;
          }
          const parsed = new Date(el.dataset.end || '');
          if (isNaN(parsed.getTime())) {
            el.hidden = true;
            return;
          }
          el._fpTarget = parsed.getTime();
        });

        this._promoCdTimer = setInterval(this.tickPromoCountdowns.bind(this), 1000);
        this.tickPromoCountdowns();
      }

      tickPromoCountdowns() {
        const now = Date.now();
        this.promoCountdowns.forEach((el) => {
          if (el.hidden || !el._fpTarget) return;
          const rem = el._fpTarget - now;
          if (rem < 0) {
            if (el.dataset.hideWhenEnded !== 'false') {
              el.hidden = true;
              return;
            }
            if (el._fpDone) return;
            el._fpDone = true;
            this.writePromoCountdown(el, 0);
            return;
          }
          this.writePromoCountdown(el, rem);
        });
      }

      writePromoCountdown(el, rem) {
        const totalSec = Math.max(0, Math.floor(rem / 1000));
        const days = Math.floor(totalSec / 86400);
        const pad2 = (n) => (n < 10 ? '0' : '') + n;
        const values = {
          d: pad2(days),
          h: pad2(Math.floor((totalSec % 86400) / 3600)),
          m: pad2(Math.floor((totalSec % 3600) / 60)),
          s: pad2(totalSec % 60),
        };
        el.querySelectorAll('[data-unit]').forEach((num) => {
          const next = values[num.dataset.unit];
          if (next === undefined || num.textContent === next) return;
          num.textContent = next;
        });
      }

      // Sticky gallery: expose the live header height as --fp-gallery-top so the
      // sticky offset (and the 1:1 stage cap) always clear the sticky header.
      bindGallerySticky() {
        this.galleryStickyHandler = () => {
          const header = document.querySelector('.wt-header');
          const headerHeight = header ? header.offsetHeight : 0;
          this.style.setProperty('--fp-gallery-top', `${headerHeight + 16}px`);
        };
        this.galleryStickyHandler();
        window.addEventListener('resize', this.galleryStickyHandler);
      }

      bindStickyVisibility() {
        if (!this.productCard || !this.stickyBar) return;
        this.setStickyVisibility(this.isAboveViewport(this.productCard));
        if (!('IntersectionObserver' in window)) return;
        this.productCardObserver = new IntersectionObserver(([entry]) => {
          this.setStickyVisibility(entry.boundingClientRect.bottom <= 0);
        }, { threshold: 0 });
        this.productCardObserver.observe(this.productCard);
      }

      isAboveViewport(node) {
        const rect = node.getBoundingClientRect();
        return rect.bottom <= 0;
      }

      setStickyVisibility(showSticky) {
        this.stickyBar.classList.toggle('is-product-card-visible', !showSticky);
        this.stickyBar.setAttribute('aria-hidden', String(!showSticky));
        if (showSticky) this.closeStickyDetails();
      }

      bindStickyToggle() {
        this.stickyToggles = [...this.querySelectorAll('[data-sticky-toggle]')];
        this.stickyDetails = this.querySelector('[data-sticky-details]');
        if (!this.stickyToggles.length || !this.stickyDetails) return;
        this.stickyToggles.forEach((toggle) => {
          toggle.addEventListener('click', () => {
            // On phones the product text is not a trigger — only the ghost chevron is.
            const isMobile = window.matchMedia('(max-width: 749px)').matches;
            if (isMobile && toggle.classList.contains('fp-sticky__product')) return;
            this.toggleStickyDetails();
          });
        });
        this.stickyOutsideHandler = (event) => {
          if (this.stickyDetails.hidden) return;
          if (this.stickyDetails.contains(event.target)) return;
          if (this.stickyToggles.some((toggle) => toggle.contains(event.target))) return;
          this.closeStickyDetails();
        };
        this.stickyEscapeHandler = (event) => {
          if (event.key === 'Escape' && !this.stickyDetails.hidden) this.closeStickyDetails();
        };
        document.addEventListener('click', this.stickyOutsideHandler);
        this.addEventListener('keydown', this.stickyEscapeHandler);
      }

      toggleStickyDetails() {
        const open = this.stickyDetails.hidden;
        this.stickyDetails.hidden = !open;
        this.stickyToggles.forEach((toggle) => {
          toggle.setAttribute('aria-expanded', String(open));
          toggle.classList.toggle('is-open', open);
        });
        if (open) this.renderStickyLines(this.currentConfiguration());
      }

      closeStickyDetails() {
        if (!this.stickyDetails) return;
        this.stickyDetails.hidden = true;
        this.stickyToggles.forEach((toggle) => {
          toggle.setAttribute('aria-expanded', 'false');
          toggle.classList.remove('is-open');
        });
      }

      bindGallery() {
        if (!this.mediaItems.length) return;
        this.galleryRoot = this.querySelector('[data-gallery]');
        this.galleryModes = this.querySelector('.fp-gallery__modes');
        const configuredGroups = new Set();
        this.modeButtons.forEach((button) => {
          const group = button.dataset.galleryMode;
          const hasMedia = this.mediaItems.some((item) => item.dataset.mediaGroup === group);
          if (hasMedia) configuredGroups.add(group);
          button.disabled = !hasMedia;
          button.hidden = !hasMedia;
          button.addEventListener('click', () => this.setMediaGroup(group));
        });
        // Surface the tab bar only when at least two groups have content —
        // a lone tab is noise, so hide the whole bar.
        if (this.galleryModes) this.galleryModes.hidden = configuredGroups.size < 2;
        this.mediaDots.forEach((dot) => dot.addEventListener('click', () => this.showMedia(dot.dataset.mediaId)));
        this.querySelector('[data-gallery-previous]')?.addEventListener('click', () => this.stepMedia(-1));
        this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.stepMedia(1));
        this.querySelectorAll('[data-gallery-zoom]').forEach((item) => {
          item.addEventListener('click', () => this.openGalleryModal(item));
          item.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            this.openGalleryModal(item);
          });
        });
        this.querySelectorAll('[data-gallery-modal-close]').forEach((button) => button.addEventListener('click', () => this.closeGalleryModal()));
        this.galleryModal?.querySelector('[data-gallery-modal-prev]')?.addEventListener('click', () => this.stepGalleryModal(-1));
        this.galleryModal?.querySelector('[data-gallery-modal-next]')?.addEventListener('click', () => this.stepGalleryModal(1));
        this.bindGalleryModalSwipe();
        this.addEventListener('keydown', (event) => {
          if (this.galleryModal?.hidden) return;
          if (event.key === 'Escape') this.closeGalleryModal();
          if (event.key === 'ArrowLeft') this.stepGalleryModal(-1);
          if (event.key === 'ArrowRight') this.stepGalleryModal(1);
        });
        const active = this.mediaItems.find((item) => item.classList.contains('is-active')) || this.mediaItems[0];
        this.activeGroup = active.dataset.mediaGroup;
        // The discount badge is tied to the first product image only — it must
        // never appear over other images, videos, or models.
        this.firstImageItem =
          this.mediaItems.find(
            (item) => item.dataset.mediaGroup === 'product' && item.querySelector('.fp-gallery__image')
          ) || null;
        this.showMedia(active.dataset.mediaId);
        this.predecodeGalleryImages(active);
      }

      predecodeGalleryImages(activeItem) {
        const images = this.mediaItems
          .filter((item) => item !== activeItem)
          .map((item) => item.querySelector('.fp-gallery__image'))
          .filter(Boolean);
        const decodeImages = () => images.forEach((image) => image.decode?.().catch(() => {}));
        if ('requestIdleCallback' in window) window.requestIdleCallback(decodeImages, { timeout: 1500 });
        else window.setTimeout(decodeImages, 250);
      }

      setMediaGroup(group) {
        const item = this.mediaItems.find((candidate) => candidate.dataset.mediaGroup === group);
        if (item) this.showMedia(item.dataset.mediaId);
      }

      showMedia(mediaId) {
        const requested = this.mediaItems.find((item) => String(item.dataset.mediaId) === String(mediaId));
        if (!requested) return;
        this.activeGroup = requested.dataset.mediaGroup;
        this.mediaItems.forEach((item) => {
          const active = item === requested;
          item.hidden = false;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-hidden', String(!active));
          if (item.hasAttribute('data-gallery-zoom')) item.tabIndex = active ? 0 : -1;
          if (!active) item.querySelector('video')?.pause();
        });
        this.mediaDots.forEach((dot) => {
          const active = dot.dataset.mediaGroup === this.activeGroup && String(dot.dataset.mediaId) === String(mediaId);
          dot.hidden = dot.dataset.mediaGroup !== this.activeGroup;
          dot.classList.toggle('is-active', active);
          dot.setAttribute('aria-current', String(active));
        });
        this.modeButtons.forEach((button) => {
          const active = button.dataset.galleryMode === this.activeGroup;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-selected', String(active));
        });
        // Discount badge: show on the first product image only.
        if (this.galleryDiscount) this.galleryDiscount.hidden = requested !== this.firstImageItem;
      }

      stepMedia(direction) {
        const items = this.mediaItems.filter((item) => item.dataset.mediaGroup === this.activeGroup);
        if (items.length < 2) return;
        const current = Math.max(0, items.findIndex((item) => item.classList.contains('is-active')));
        this.showMedia(items[(current + direction + items.length) % items.length].dataset.mediaId);
      }

      updateGalleryImage(imageUrl, altText) {
        if (!imageUrl) return;
        const mediaItem = this.mediaItems.find((item) => item.dataset.mediaGroup === 'product');
        if (!mediaItem) return;
        const source = mediaItem.querySelector('source[type="image/webp"]');
        const img = mediaItem.querySelector('.fp-gallery__image');
        if (!img) return;
        const baseUrl = imageUrl.replace(/_\d+x\./, '_{w}x.');
        const widths = [480, 720, 960, 1200, 1400];
        const srcset = widths.map((w) => `${baseUrl.replace('{w}', w)} ${w}w`).join(', ');
        const sizes = '(min-width: 1157px) 700px, (min-width: 990px) 60vw, (min-width: 750px) 720px, 100vw';
        if (source) {
          source.setAttribute('srcset', srcset);
          source.setAttribute('sizes', sizes);
        }
        // Update both src and srcset on <img> so the browser doesn't
        // keep serving the old image from the previous srcset.
        img.setAttribute('srcset', srcset);
        img.setAttribute('sizes', sizes);
        img.src = baseUrl.replace('{w}', '1400');
        if (altText) img.alt = altText;
        mediaItem.setAttribute('data-zoom-src', baseUrl.replace('{w}', '2400'));
        if (altText) mediaItem.setAttribute('data-zoom-alt', altText);
      }

      bindTabs() {
        this.tabButtons.forEach((button) => button.addEventListener('click', () => this.setMode(button.dataset.configTab || button.dataset.mode)));
      }

      setMode(mode) {
        if (this.mode === mode) return;
        this.mode = mode;
        this.tabButtons.forEach((button) => {
          const active = button.dataset.configTab === mode;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-selected', String(active));
        });
        this.panels.forEach((panel) => {
          const active = panel.dataset.configPanel === mode;
          panel.hidden = !active;
          panel.classList.toggle('is-active', active);
        });
        // Reset accessory quantities when switching modes to prevent duplicates
        // (rec panel and custom panel have separate accessory inputs for the
        // same variants; without reset, both would be counted in the summary).
        this.accessories.forEach((input) => {
          this.accessoryQuantities.set(input, 0);
          const label = input.closest('.fp-addon-toggle');
          const output = label?.querySelector('[data-accessory-qty-output]');
          if (output) output.textContent = '0';
          if (input) input.checked = false;
          label?.classList.remove('is-selected');
          label?.setAttribute('aria-pressed', 'false');
        });
        // Gifts only exist in the custom panel; reset every card so flags do
        // not leak between modes (re-entering Custom = fresh auto-unlock).
        this.resetGifts();
        this.updateSummary();
      }

      bindConfiguration() {
        this.recommendations.forEach((card) => card.addEventListener('click', () => {
          this.recommendations.forEach((candidate) => {
            const selected = candidate === card;
            candidate.classList.toggle('is-selected', selected);
            candidate.setAttribute('aria-checked', String(selected));
          });
          this.updateGalleryImage(card.dataset.image, card.dataset.title);
          this.updateSummary();
          this.updateUrl();
        }));
        this.bindChoiceGroup(this.recSolarOptions);
        this.bindChoiceGroup(this.customSolarOptions);
        this.recBattery?.addEventListener('change', () => {
          if (this.recBattery?.checked) {
            const recBatteryImage = this.lookupMetaImage(this.galleryMaps.battery, 'qty', 1) || this.recBattery.dataset.image;
            if (recBatteryImage) this.updateGalleryImage(recBatteryImage, this.recBattery.dataset.title);
          }
          this.updateSummary();
        });
        this.accessories.forEach((input) => {
          this.accessoryQuantities.set(input, 0);
          const label = input.closest('.fp-addon-toggle');
          const minus = label?.querySelector('[data-accessory-qty-minus]');
          const plus = label?.querySelector('[data-accessory-qty-plus]');
          const output = label?.querySelector('[data-accessory-qty-output]');
          minus?.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            input.blur();
            this.changeAccessoryQuantity(input, -1);
          });
          plus?.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            input.blur();
            this.changeAccessoryQuantity(input, 1);
          });
          label?.addEventListener('click', (event) => {
            if (event.target.closest('.fp-qty')) {
              event.preventDefault();
            }
          }, true);
          input.addEventListener('change', () => {
            const qty = input.checked ? 1 : 0;
            this.accessoryQuantities.set(input, qty);
            if (output) output.textContent = String(qty);
            label?.classList.toggle('is-selected', qty > 0);
            this.updateSummary();
          });
        });
        this.querySelector('[data-qty-minus]')?.addEventListener('click', () => this.changeBatteryQuantity(-1));
        this.querySelector('[data-qty-plus]')?.addEventListener('click', () => this.changeBatteryQuantity(1));
        this.querySelector('[data-main-qty-minus]')?.addEventListener('click', () => this.changeMainQuantity(-1));
        this.querySelector('[data-main-qty-plus]')?.addEventListener('click', () => this.changeMainQuantity(1));
        // Gift cards only exist in the custom panel; bind every card.
        this.bindGiftCards();
        const batteryCard = this.battery?.querySelector('.fp-builder-row');
        batteryCard?.addEventListener('click', (event) => {
          if (event.target.closest('button')) return;
          this.setBatteryQuantity(this.batteryQuantity > 0 ? 0 : 1);
        });
        batteryCard?.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          this.setBatteryQuantity(this.batteryQuantity > 0 ? 0 : 1);
        });
        this.updateBatteryCard();
      }

      bindChoiceGroup(options) {
        options.forEach((option) => option.addEventListener('click', () => {
          options.forEach((candidate) => candidate.classList.toggle('is-selected', candidate === option));
          const panelCount = this.number(option.dataset.panelCount);
          const solarImage = this.lookupMetaImage(this.galleryMaps.solar, 'panel_count', panelCount) || option.dataset.image;
          if (solarImage) this.updateGalleryImage(solarImage, option.dataset.title);
          this.updateSummary();
          this.updateUrl();
        }));
      }

      changeBatteryQuantity(delta) {
        const max = this.number(this.battery?.dataset.max, 5);
        this.setBatteryQuantity(Math.max(0, Math.min(max, this.batteryQuantity + delta)));
      }

      setBatteryQuantity(quantity) {
        const max = this.number(this.battery?.dataset.max, 5);
        this.batteryQuantity = Math.max(0, Math.min(max, quantity));
        const output = this.querySelector('[data-qty-output]');
        if (output) output.textContent = String(this.batteryQuantity);
        this.updateBatteryCard();
        const batteryImage = this.lookupMetaImage(this.galleryMaps.battery, 'qty', this.batteryQuantity)
          || (this.batteryQuantity > 0 ? this.battery?.dataset.image : null);
        if (batteryImage) this.updateGalleryImage(batteryImage, this.battery?.dataset.title);
        this.updateSummary();
        this.updateUrl();
      }

      updateBatteryCard() {
        const selected = this.batteryQuantity > 0;
        this.battery?.classList.toggle('is-selected', selected);
        this.battery?.querySelector('.fp-builder-row')?.setAttribute('aria-pressed', String(selected));
      }

      changeMainQuantity(delta) {
        this.mainQuantity = Math.max(1, this.mainQuantity + delta);
        const output = this.querySelector('[data-main-qty-output]');
        if (output) output.textContent = String(this.mainQuantity);
        this.updateSummary();
        this.updateUrl();
      }

      /* ---------- Gift cards (custom mode only, independent per card) ---------- */

      bindGiftCards() {
        this.gifts.forEach((card) => {
          card.addEventListener('click', (event) => {
            if (event.target.closest('button')) return;
            this.toggleGift(card);
          });
          card.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            this.toggleGift(card);
          });
        });
      }

      giftStateFor(card) {
        if (!this.giftState.has(card)) {
          this.giftState.set(card, { selected: false, dismissed: false });
        }
        return this.giftState.get(card);
      }

      // Full reset — used on mode switches (no gift state leaks across tabs)
      resetGifts() {
        this.gifts.forEach((card) => {
          this.giftState.set(card, { selected: false, dismissed: false });
          delete card.dataset.wasEligible;
          card.classList.remove('is-selected', 'is-eligible', 'is-disabled');
          card.setAttribute('aria-pressed', 'false');
          card.setAttribute('aria-disabled', 'false');
        });
        this.activeGifts = [];
      }

      isGiftConfigured(card) {
        return /^\d{8,}$/.test(card.dataset.variantId || '') && card.dataset.available === 'true';
      }

      isGiftEligible(card, config) {
        if (this.mode !== 'custom' || !this.isGiftConfigured(card)) return false;
        const meetsSpend = config.price >= this.number(card.dataset.minSpend);
        const meetsBattery = card.dataset.requireBattery !== 'true' || config.batteryCount > 0;
        return meetsSpend && meetsBattery;
      }

      // User click/keyboard toggle — disabled cards stay clickable but do nothing
      toggleGift(card) {
        if (this.mode !== 'custom') return;
        if (card.classList.contains('is-disabled')) return;
        const state = this.giftStateFor(card);
        if (state.selected) {
          // Manual uncheck: remember dismissal — no auto re-check afterwards
          state.selected = false;
          state.dismissed = true;
        } else {
          state.selected = true;
          state.dismissed = false;
        }
        this.updateSummary();
      }

      giftStatusText(card, eligible, selected, config) {
        const requirement = card.dataset.requirementText || '';
        if (selected) return 'Wird automatisch hinzugefügt';
        if (requirement) return requirement;
        if (!this.isGiftConfigured(card)) return '';
        if (!eligible) {
          if (card.dataset.requireBattery === 'true' && config.batteryCount === 0) {
            return 'Nur mit Erweiterungsakku erhältlich';
          }
          return `Verfügbar ab ${this.formatMoney(this.number(card.dataset.minSpend))}`;
        }
        return 'Jetzt kostenlos hinzufügen';
      }

      // Single source of truth: reconcile every gift card with the current
      // configuration. Returns the list of eligible + selected gifts.
      syncGifts(config) {
        if (this.mode !== 'custom' || !this.gifts.length) {
          this.activeGifts = [];
          return this.activeGifts;
        }
        const active = [];
        this.gifts.forEach((card) => {
          const state = this.giftStateFor(card);
          const wasEligible = card.dataset.wasEligible === 'true';
          const eligible = this.isGiftEligible(card, config);

          if (eligible && !wasEligible && !state.dismissed) {
            // Freshly unlocked: auto-check once
            state.selected = true;
          } else if (!eligible) {
            // Locked: cannot keep the gift (dismissal flag is preserved)
            state.selected = false;
          }
          // Continuously eligible: state is fully user-controlled.

          card.dataset.wasEligible = String(eligible);
          card.classList.toggle('is-eligible', eligible);
          card.classList.toggle('is-selected', eligible && state.selected);
          card.classList.toggle('is-disabled', !eligible);
          card.setAttribute('aria-pressed', String(eligible && state.selected));
          card.setAttribute('aria-disabled', String(!eligible));
          const status = card.querySelector('[data-gift-status]');
          if (status) {
            status.textContent = this.giftStatusText(card, eligible, eligible && state.selected, config);
          }
          if (eligible && state.selected) active.push(card);
        });
        this.activeGifts = active;
        return active;
      }

      changeAccessoryQuantity(input, delta) {
        const current = this.accessoryQuantities.get(input) || 0;
        const next = Math.max(0, current + delta);
        this.accessoryQuantities.set(input, next);
        const label = input.closest('.fp-addon-toggle');
        const output = label?.querySelector('[data-accessory-qty-output]');
        if (output) output.textContent = String(next);
        input.checked = next > 0;
        label?.classList.toggle('is-selected', next > 0);
        this.updateSummary();
      }

      accessoryQuantity(input) {
        return this.accessoryQuantities.get(input) || 0;
      }

      selected(selector, collection) {
        return collection.find((item) => item.matches(selector)) || collection[0] || null;
      }

      currentConfiguration() {
        if (this.mode === 'recommended') {
          const recommendation = this.selected('.is-selected', this.recommendations);
          const solar = this.selected('.is-selected', this.recSolarOptions);
          const extraBattery = this.recBattery?.checked ? 1 : 0;
          const selectedAccessories = this.accessories.filter((input) => this.accessoryQuantity(input) > 0);
          const recVId = recommendation?.dataset.variantId;
          const basePrice = this.number(recommendation?.dataset.price);
          const baseCompare = this.number(recommendation?.dataset.compare, basePrice);
          const solarVId = solar?.dataset.variantId;
          const solarPrice = this.number(solar?.dataset.price) * this.number(solar?.dataset.cartQty);
          const solarCompare = this.number(solar?.dataset.compare, this.number(solar?.dataset.price)) * this.number(solar?.dataset.cartQty);
          const batteryVId = this.recBattery?.dataset.variantId;
          const batteryPrice = extraBattery * this.number(this.recBattery?.dataset.price);
          const batteryCompare = extraBattery * this.number(this.recBattery?.dataset.compare, this.number(this.recBattery?.dataset.price));
          const accessoryPrice = selectedAccessories.reduce((sum, item) => sum + this.number(item.dataset.price) * this.accessoryQuantity(item), 0);
          const accessoryCompare = selectedAccessories.reduce((sum, item) => sum + this.number(item.dataset.compare, this.number(item.dataset.price)) * this.accessoryQuantity(item), 0);
          return {
            title: recommendation?.dataset.title || '',
            price: basePrice + solarPrice + batteryPrice + accessoryPrice,
            compare: baseCompare + solarCompare + batteryCompare + accessoryCompare,
            capacity: this.number(recommendation?.dataset.capacity) + extraBattery * this.number(this.recBattery?.dataset.capacity),
            solar: this.number(recommendation?.dataset.solar),
            output: this.number(recommendation?.dataset.output),
            batteryLife: recommendation?.dataset.batteryLife || '',
            batteryCount: extraBattery,
            image: recommendation?.dataset.image || '',
            items: this.recommendationItems(recommendation, solar, extraBattery, selectedAccessories),
            summary: this.recommendationSummary(recommendation, solar, extraBattery, selectedAccessories),
          };
        }

        const solar = this.selected('.is-selected', this.customSolarOptions);
        const stackMain = this.mainProduct?.dataset.stackCapacity === 'true';
        const mainVId = this.mainProduct?.dataset.variantId;
        const basePrice = this.number(this.mainProduct?.dataset.price) * this.mainQuantity;
        const baseCompare = this.number(this.mainProduct?.dataset.compare, this.number(this.mainProduct?.dataset.price)) * this.mainQuantity;
        const batteryVId = this.battery?.dataset.variantId;
        const batteryPrice = this.number(this.battery?.dataset.price) * this.batteryQuantity;
        const batteryCompare = this.number(this.battery?.dataset.compare, this.number(this.battery?.dataset.price)) * this.batteryQuantity;
        const solarVId = solar?.dataset.variantId;
        const solarPrice = this.number(solar?.dataset.price) * this.number(solar?.dataset.cartQty);
        const solarCompare = this.number(solar?.dataset.compare, this.number(solar?.dataset.price)) * this.number(solar?.dataset.cartQty);
        const selectedAccessories = this.accessories.filter((input) => this.accessoryQuantity(input) > 0);
        return {
          title: this.configurationTitle(solar, selectedAccessories),
          price: basePrice + batteryPrice + solarPrice + selectedAccessories.reduce((sum, item) => sum + this.number(item.dataset.price) * this.accessoryQuantity(item), 0),
          compare: baseCompare + batteryCompare + solarCompare + selectedAccessories.reduce((sum, item) => sum + this.number(item.dataset.compare, this.number(item.dataset.price)) * this.accessoryQuantity(item), 0),
          capacity: (stackMain ? this.mainQuantity : 1) * this.number(this.mainProduct?.dataset.capacity) + this.batteryQuantity * this.number(this.battery?.dataset.capacity),
          solar: this.number(this.mainProduct?.dataset.solar),
          output: (stackMain ? this.mainQuantity : 1) * this.number(this.mainProduct?.dataset.output, 2500),
          batteryCount: this.batteryQuantity,
          image: this.mainProduct?.dataset.image || '',
          items: this.customItems(solar, selectedAccessories),
          summary: this.customSummary(solar, selectedAccessories),
        };
      }

      recommendationItems(recommendation, solar, extraBattery, accessories) {
        const items = [];
        this.pushItem(items, recommendation?.dataset.variantId, 1, recommendation?.dataset.title, this.t('roleRecommendation', 'Empfehlung'));
        this.pushItem(items, solar?.dataset.variantId, this.number(solar?.dataset.cartQty), solar?.dataset.title, this.t('roleSolar', 'Solarmodul'));
        if (extraBattery) this.pushItem(items, this.recBattery?.dataset.variantId, 1, this.recBattery?.dataset.title, this.t('roleBattery', 'Erweiterungsakku'));
        accessories?.forEach((item) => this.pushItem(items, item.dataset.variantId, this.accessoryQuantity(item), item.dataset.title, this.t('roleAccessory', 'Zubehör')));
        return items;
      }

      customItems(solar, accessories) {
        const items = [];
        this.pushItem(items, this.mainProduct?.dataset.variantId, this.mainQuantity, this.mainProduct?.dataset.title, this.t('roleMain', 'Hauptprodukt'));
        this.pushItem(items, this.battery?.dataset.variantId, this.batteryQuantity, this.battery?.dataset.title, this.t('roleBattery', 'Erweiterungsakku'));
        this.pushItem(items, solar?.dataset.variantId, this.number(solar?.dataset.cartQty), solar?.dataset.title, this.t('roleSolar', 'Solarmodul'));
        accessories.forEach((item) => this.pushItem(items, item.dataset.variantId, this.accessoryQuantity(item), item.dataset.title, this.t('roleAccessory', 'Zubehör')));
        return items;
      }

      pushItem(items, id, quantity, title, role) {
        if (!quantity) return;
        items.push({
          id: String(id || ''),
          quantity,
          title: title || role,
          role,
        });
      }

      recommendationSummary(recommendation, solar, extraBattery, accessories) {
        const recVariantId = recommendation?.dataset.variantId;
        const basePrice = this.number(recommendation?.dataset.price);
        const baseCompare = this.number(recommendation?.dataset.compare, basePrice);
        const contains = this.parseContains(recommendation?.dataset.contains);
        const solarQty = this.number(solar?.dataset.cartQty);

        const addons = [];
        if (solarQty) {
          const vId = solar.dataset.variantId;
          addons.push({
            variantId: vId,
            title: solar.dataset.title,
            image: solar.dataset.image || '',
            price: this.number(solar.dataset.price) * solarQty,
            compare: this.number(solar.dataset.compare, this.number(solar.dataset.price)) * solarQty,
            qty: this.number(solar.dataset.panelCount) || solarQty,
            removeKind: 'click',
            removeEl: this.recSolarOptions[0],
          });
        }
        if (extraBattery && this.recBattery) {
          const vId = this.recBattery.dataset.variantId;
          addons.push({
            variantId: vId,
            title: this.recBattery.dataset.title,
            image: this.recBattery.dataset.image || '',
            price: this.number(this.recBattery.dataset.price),
            compare: this.number(this.recBattery.dataset.compare, this.number(this.recBattery.dataset.price)),
            qty: 1,
            removeKind: 'uncheck',
            removeEl: this.recBattery,
          });
        }
        accessories?.forEach((item) => {
          const qty = this.accessoryQuantity(item);
          if (qty <= 0) return;
          const vId = item.dataset.variantId;
          addons.push({
            variantId: vId,
            title: item.dataset.title,
            image: item.dataset.image || '',
            price: this.number(item.dataset.price) * qty,
            compare: this.number(item.dataset.compare, this.number(item.dataset.price)) * qty,
            qty,
            removeKind: 'accessory-qty',
            removeEl: item,
          });
        });

        return {
          kit: {
            variantId: recVariantId,
            title: recommendation?.dataset.title || '',
            image: recommendation?.dataset.image || '',
            price: basePrice,
            compare: baseCompare,
            contains,
          },
          addons,
        };
      }

      customSummary(solar, accessories) {
        const mainVariantId = this.mainProduct?.dataset.variantId;
        const basePrice = this.number(this.mainProduct?.dataset.price) * this.mainQuantity;
        const baseCompare = this.number(this.mainProduct?.dataset.compare, this.number(this.mainProduct?.dataset.price)) * this.mainQuantity;
        const addons = [];

        if (this.batteryQuantity && this.battery) {
          const vId = this.battery.dataset.variantId;
          addons.push({
            variantId: vId,
            title: this.battery.dataset.title,
            image: this.battery.dataset.image || '',
            price: this.number(this.battery.dataset.price) * this.batteryQuantity,
            compare: this.number(this.battery.dataset.compare, this.number(this.battery.dataset.price)) * this.batteryQuantity,
            qty: this.batteryQuantity,
            removeKind: 'battery',
            removeEl: null,
          });
        }

        const solarQty = this.number(solar?.dataset.cartQty);
        if (solarQty) {
          const vId = solar.dataset.variantId;
          addons.push({
            variantId: vId,
            title: solar.dataset.title,
            image: solar.dataset.image || '',
            price: this.number(solar.dataset.price) * solarQty,
            compare: this.number(solar.dataset.compare, this.number(solar.dataset.price)) * solarQty,
            qty: this.number(solar.dataset.panelCount) || solarQty,
            removeKind: 'click',
            removeEl: this.customSolarOptions[0],
          });
        }

        accessories.forEach((item) => {
          const qty = this.accessoryQuantity(item);
          const vId = item.dataset.variantId;
          addons.push({
            variantId: vId,
            title: item.dataset.title,
            image: item.dataset.image || '',
            price: this.number(item.dataset.price) * qty,
            compare: this.number(item.dataset.compare, this.number(item.dataset.price)) * qty,
            qty,
            removeKind: 'accessory-qty',
            removeEl: item,
          });
        });

        return {
          kit: {
            variantId: mainVariantId,
            title: this.mainProduct?.dataset.title || this.productCard?.dataset.productTitle || '',
            image: this.mainProduct?.dataset.image || '',
            price: basePrice,
            compare: baseCompare,
            contains: this.customContains(),
          },
          addons,
        };
      }

      pushContain(contains, title, qty) {
        if (!qty || !title) return;
        contains.push({ title, qty });
      }

      parseContains(value) {
        if (!value) return [];
        try {
          const parsed = JSON.parse(value);
          if (!Array.isArray(parsed)) return [];
          return parsed
            .filter((item) => item && item.title && this.number(item.qty) > 0)
            .map((item) => ({ title: String(item.title), qty: this.number(item.qty) }));
        } catch (error) {
          return [];
        }
      }

      customContains() {
        const contains = [];
        this.pushContain(contains, this.mainProduct?.dataset.title || this.productCard?.dataset.productTitle, this.mainQuantity);
        if (this.batteryQuantity && this.battery) {
          this.pushContain(contains, this.battery.dataset.title, this.batteryQuantity);
        }
        return contains;
      }

      configurationTitle(solar, accessories) {
        const parts = ['Venus D'];
        if (this.mainQuantity > 1) parts.push(`${this.mainQuantity}x ${this.t('labelMainDevice', 'Hauptgerät')}`);
        if (this.batteryQuantity) parts.push(`${this.batteryQuantity}x ${this.t('roleBattery', 'Erweiterungsakku')}`);
        if (this.number(solar?.dataset.panelCount)) parts.push(`${solar.dataset.panelCount}x ${this.t('roleSolar', 'Solarmodul')}`);
        accessories.forEach((item) => parts.push(`${this.accessoryQuantity(item)}x ${item.dataset.title}`));
        return parts.join(' + ');
      }

      updateSummary() {
        const config = this.currentConfiguration();
        this.syncGifts(config);
        this.setText('[data-sticky-price]', this.formatMoney(config.price));
        this.setText('[data-sticky-compare]', this.formatMoney(config.compare), config.compare <= config.price);
        this.setText('[data-sticky-saving]', this.formatMoney(-Math.max(0, config.compare - config.price)), config.compare <= config.price);
        this.setText('[data-card-price]', this.formatMoney(config.price));
        // Expose the raw price in cents so other widgets (e.g. the Smile points hint) can recompute live
        const cardPriceNode = this.querySelector('[data-card-price]');
        if (cardPriceNode) cardPriceNode.dataset.priceCents = String(config.price);
        this.setText('[data-card-compare]', this.formatMoney(config.compare), config.compare <= config.price);
        const savingAmount = Math.max(0, config.compare - config.price);
        this.setText('[data-card-discount]', savingAmount ? this.formatMoney(-savingAmount) : '', !(config.compare > config.price));
        this.renderCard(config);
        this.renderStickyLines(config);
        this.updatePackingList(config);
        this.updateSpecs(config);
      }

      updateSpecs(config) {
        const root = this.querySelector(`[data-specs="${this.mode}"]`);
        if (!root) return;
        const apply = (selector, value, show) => {
          const node = root.querySelector(selector);
          if (!node) return;
          node.textContent = value;
          const card = node.closest('.fp-spec');
          if (card) card.hidden = !show;
        };
        apply('[data-stat-capacity]', this.formatPower(config.capacity, true), config.capacity > 0);
        apply('[data-stat-solar]', this.formatPower(config.solar), config.solar > 0);
        apply('[data-stat-output]', this.formatPower(config.output), config.output > 0);
        // Battery life is a per-recommendation value; custom mode keeps its
        // Liquid-rendered stat untouched (config.batteryLife stays undefined).
        if (config.batteryLife !== undefined) {
          const life = String(config.batteryLife || '').trim();
          const node = root.querySelector('[data-stat-battery-life]');
          if (node) {
            node.textContent = life;
            const card = node.closest('.fp-spec');
            if (card) card.hidden = !life;
          }
        }
      }

      // Packing list integration: pass all currently configured items (main product
      // + battery + solar + addons + gifts) to the global packing list section API.
      // Dynamic items use absolute quantities (mainQty=1); static items scale with the main quantity.
      updatePackingList(config) {
        if (typeof window.updatePackingList !== 'function') return;
        const { kit, addons } = config.summary || { kit: null, addons: [] };
        const entries = [];
        if (kit && kit.image) {
          entries.push({
            image: kit.image,
            title: kit.title || '',
            qty: this.mode === 'custom' ? this.mainQuantity : 1,
          });
        }
        (addons || []).forEach((addon) => {
          if (addon.qty > 0 && addon.image) {
            entries.push({ image: addon.image, title: addon.title || '', qty: addon.qty });
          }
        });
        this.activeGifts.forEach((card) => {
          if (card.dataset.image) {
            entries.push({ image: card.dataset.image, title: card.dataset.title || '', qty: 1 });
          }
        });
        window.updatePackingList(entries, 1);
        if (typeof window.refreshPackingListUI === 'function') {
          window.refreshPackingListUI(this.mode === 'custom' ? this.mainQuantity : 1);
        }
      }

      renderCard(config) {
        const container = this.querySelector('[data-card-lines]');
        if (!container) return;
        const { kit, addons } = config.summary || { kit: null, addons: [] };
        container.innerHTML = this.buildSummaryHtml(config, false);
        container.hidden = !(kit || addons.length || this.activeGifts.length);
        container.querySelectorAll('[data-line-remove]').forEach((button) => {
          button.addEventListener('click', () => {
            const key = button.dataset.lineRemove;
            if (key.startsWith('gift-')) {
              // Single source of truth: gift removal goes through toggleGift, exactly like unchecking the card
              const giftCard = this.gifts[Number(key.slice(5))];
              const state = giftCard ? this.giftStateFor(giftCard) : null;
              if (giftCard && state?.selected) this.toggleGift(giftCard);
            } else {
              this.removeAddon(addons[Number(key)]);
            }
          });
        });
      }

      // The summary card and sticky drawer share this render. When readOnly, omit remove buttons and per-item prices.
      buildSummaryHtml(config, readOnly) {
        const { kit, addons } = config.summary || { kit: null, addons: [] };
        const html = [];
        if (kit) html.push(this.renderKitLine(kit));
        addons.forEach((addon, index) => html.push(this.renderAddonLine(addon, readOnly ? null : index, readOnly)));
        if (this.mode === 'custom') {
          this.activeGifts.forEach((card) => html.push(this.renderGiftLine(card, readOnly)));
        }
        return html.join('');
      }

      renderKitLine(kit) {
        const contains = kit.contains
          .map((item) => `<li><span>${this.escapeHtml(item.title)}</span><em>&times;${item.qty}</em></li>`)
          .join('');
        const compareLine = kit.compare > kit.price
          ? `<s class="fp-line__compare">${this.formatMoney(kit.compare)}</s>`
          : '';
        const prices = `<div class="fp-line__prices"><span class="fp-line__price">${this.formatMoney(kit.price)}</span>${compareLine}</div>`;
        return `<div class="fp-line fp-line--kit">${this.lineImage(kit.image, kit.title)}<div class="fp-line__body"><strong class="fp-line__title">${this.escapeHtml(kit.title)}</strong>${prices}${contains ? `<ul class="fp-line__contains">${contains}</ul>` : ''}</div></div>`;
      }

      renderAddonLine(addon, index = null, readOnly = false) {
        const removeLabel = this.escapeHtml(this.productCard?.dataset.removeLabel || this.t('remove', 'Entfernen'));
        const title = `<strong class="fp-line__title">${this.escapeHtml(addon.title)}</strong>`;
        const removeButton = readOnly || index === null ? '' : `<button type="button" class="fp-line__remove" data-line-remove="${index}">${removeLabel}</button>`;
        const priceLine = `<span class="fp-line__price">${this.formatMoney(addon.price)}</span>`;
        return `<div class="fp-line fp-line--addon">${this.lineImage(addon.image, addon.title)}<div class="fp-line__body">${title}${priceLine}</div><div class="fp-line__side">${removeButton}<em class="fp-line__qty">&times;${addon.qty}</em></div></div>`;
      }

      renderGiftLine(card, readOnly = false) {
        const giftIndex = this.gifts.indexOf(card);
        const removeLabel = this.escapeHtml(this.productCard?.dataset.removeLabel || this.t('remove', 'Entfernen'));
        const removeButton = readOnly ? '' : `<button type="button" class="fp-line__remove" data-line-remove="gift-${giftIndex}">${removeLabel}</button>`;
        const priceLine = `<span class="fp-line__price fp-line__price--free">${this.escapeHtml(this.dataset.freeLabel || this.t('freeLabel', 'GRATIS'))}</span>`;
        return `<div class="fp-line fp-line--gift">${this.lineImage(card.dataset.image, card.dataset.title)}<div class="fp-line__body"><strong class="fp-line__title">${this.escapeHtml(card.dataset.title || '')}</strong>${priceLine}<span class="fp-line__gift-label">${this.escapeHtml(this.t('roleGift', 'Gratiszugabe'))}</span></div><div class="fp-line__side">${removeButton}<em class="fp-line__qty">&times;1</em></div></div>`;
      }

      renderStickyLines(config) {
        const container = this.querySelector('[data-sticky-lines]');
        if (!container) return;
        const { kit, addons } = config.summary || { kit: null, addons: [] };
        container.innerHTML = this.buildSummaryHtml(config, true);
        // Update the count badge on the mobile list button
        const countEl = this.querySelector('[data-sticky-count]');
        if (countEl) {
          countEl.textContent = String(addons.length + (kit ? 1 : 0) + this.activeGifts.length);
        }
      }

      lineImage(image, alt) {
        if (image) {
          return `<span class="fp-line__image"><img src="${this.escapeHtml(image)}" width="72" height="72" loading="lazy" alt="${this.escapeHtml(alt)}"></span>`;
        }
        return '<span class="fp-line__image fp-line__image--empty" aria-hidden="true"></span>';
      }

      t(key, fallback) {
        return (this.i18n && this.i18n[key]) || fallback;
      }

      escapeHtml(value) {
        return String(value ?? '').replace(/[&<>"']/g, (char) => ({
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#39;',
        }[char]));
      }

      removeAddon(addon) {
        if (!addon) return;
        if (addon.removeKind === 'battery') {
          this.setBatteryQuantity(0);
        } else if (addon.removeKind === 'uncheck' && addon.removeEl) {
          addon.removeEl.checked = false;
          addon.removeEl.dispatchEvent(new Event('change', { bubbles: true }));
        } else if (addon.removeKind === 'accessory-qty' && addon.removeEl) {
          this.changeAccessoryQuantity(addon.removeEl, -this.accessoryQuantity(addon.removeEl));
        } else if (addon.removeKind === 'click' && addon.removeEl) {
          addon.removeEl.click();
        }
      }

      bindPurchase() {
        this.form?.addEventListener('submit', (event) => {
          event.preventDefault();
          this.requestPurchase('cart');
        });
        this.buyButtons.forEach((button) => button.addEventListener('click', () => this.requestPurchase('buy')));
      }

      requestPurchase(action) {
        this.clearMessage();
        this.pendingAction = action;
        if (this.taxModal && !this.taxConfirmed) {
          const stored = localStorage.getItem('fp-tax-confirmed');
          if (stored === 'true') {
            this.taxConfirmed = true;
          }
        }
        if (this.taxModal && !this.taxConfirmed) {
          this.openTaxModal();
          return;
        }
        this.completePurchase(action);
      }

      bindTaxModal() {
        this.querySelectorAll('[data-tax-close]').forEach((button) => button.addEventListener('click', () => this.closeTaxModal()));
        this.taxCheckbox?.addEventListener('change', () => {
          const confirm = this.querySelector('[data-tax-confirm]');
          if (confirm) confirm.disabled = !this.taxCheckbox.checked;
        });
        this.querySelector('[data-tax-confirm]')?.addEventListener('click', () => {
          if (!this.taxCheckbox?.checked) return;
          this.taxConfirmed = true;
          localStorage.setItem('fp-tax-confirmed', 'true');
          this.closeTaxModal();
          if (this.pendingAction) this.completePurchase(this.pendingAction);
        });
        this.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && !this.taxModal?.hidden) this.closeTaxModal();
        });
        this.querySelectorAll('[data-purchase-message-close]').forEach((button) => {
          button.addEventListener('click', () => this.clearMessage());
        });
      }

      openGalleryModal(item) {
        if (!this.galleryModal || !this.galleryModalImage || !item?.dataset.zoomSrc) return;
        // Gather the image set at open time (DOM may have changed since init).
        // Only zoomable images enter the lightbox; videos stay inline.
        this.modalItems = this.mediaItems.filter(
          (candidate) => candidate.dataset.zoomSrc && candidate.dataset.mediaId
        );
        if (!this.modalItems.length) return;
        // Locate by unique media id — avoid src matching (duplicate images possible).
        const startId = String(item.dataset.mediaId);
        let index = this.modalItems.findIndex((candidate) => String(candidate.dataset.mediaId) === startId);
        if (index < 0) index = 0;
        this.galleryModalIndex = index;
        this.galleryModalTrigger = item;
        this.buildGalleryModalThumbs();
        this.renderGalleryModal();
        this.galleryModal.hidden = false;
        this.galleryModal.setAttribute('aria-hidden', 'false');
        this.galleryModal.classList.add('is-open');
        document.body.classList.add('fp-modal-open');
        this.galleryModal.querySelector('.fp-gallery-modal__close')?.focus();
      }

      buildGalleryModalThumbs() {
        if (!this.galleryModalThumbs) return;
        const hasMultiple = this.modalItems.length > 1;
        this.galleryModalThumbs.hidden = !hasMultiple;
        if (!hasMultiple) {
          this.galleryModalThumbs.innerHTML = '';
          return;
        }
        // Prefer the already-loaded inline preview src (small); fall back to zoom src.
        this.galleryModalThumbs.innerHTML = this.modalItems.map((candidate, index) => {
          const thumbSrc = candidate.querySelector('.fp-gallery__image')?.src || candidate.dataset.zoomSrc;
          const alt = this.escapeHtml(candidate.dataset.zoomAlt || '');
          return `<button type="button" class="fp-gallery-modal__thumb" data-modal-thumb="${index}" aria-label="${this.escapeHtml(this.t('imageItem', 'Bild {n}').replace('{n}', index + 1))}"><img src="${this.escapeHtml(thumbSrc)}" alt="${alt}" loading="lazy" decoding="async"></button>`;
        }).join('');
        this.galleryModalThumbs.querySelectorAll('[data-modal-thumb]').forEach((button) => {
          button.addEventListener('click', () => {
            const target = Number(button.dataset.modalThumb);
            if (Number.isInteger(target)) {
              this.galleryModalIndex = target;
              this.renderGalleryModal();
            }
          });
        });
      }

      renderGalleryModal() {
        const item = this.modalItems?.[this.galleryModalIndex];
        if (!item) return;
        this.galleryModalImage.src = item.dataset.zoomSrc;
        this.galleryModalImage.alt = item.dataset.zoomAlt || '';
        const hasMultiple = this.modalItems.length > 1;
        this.galleryModal.querySelectorAll('.fp-gallery-modal__arrow').forEach((arrow) => {
          arrow.hidden = !hasMultiple;
        });
        this.galleryModalThumbs?.querySelectorAll('.fp-gallery-modal__thumb').forEach((button, index) => {
          const active = index === this.galleryModalIndex;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-current', String(active));
        });
        // Keep the active thumb visible in the scrollable strip.
        const activeThumb = this.galleryModalThumbs?.children[this.galleryModalIndex];
        activeThumb?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        // Keep the inline gallery in sync with the lightbox.
        this.showMedia(item.dataset.mediaId);
      }

      stepGalleryModal(direction) {
        if (!this.modalItems?.length || this.galleryModal?.hidden) return;
        if (this.modalItems.length < 2) return;
        const total = this.modalItems.length;
        // Infinite loop across all images (product + usage), regardless of group.
        this.galleryModalIndex = (this.galleryModalIndex + direction + total) % total;
        this.renderGalleryModal();
      }

      bindGalleryModalSwipe() {
        if (!this.galleryModal) return;
        let startX = 0;
        let startY = 0;
        let tracking = false;
        this.galleryModal.addEventListener('touchstart', (event) => {
          if (event.touches.length !== 1) return;
          startX = event.touches[0].clientX;
          startY = event.touches[0].clientY;
          tracking = true;
        }, { passive: true });
        this.galleryModal.addEventListener('touchend', (event) => {
          if (!tracking) return;
          tracking = false;
          const touch = event.changedTouches[0];
          const dx = touch.clientX - startX;
          const dy = touch.clientY - startY;
          // Horizontal swipe must dominate vertical scroll.
          if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.4) {
            this.stepGalleryModal(dx < 0 ? 1 : -1);
          }
        }, { passive: true });
      }

      closeGalleryModal() {
        if (!this.galleryModal) return;
        this.galleryModal.classList.remove('is-open');
        this.galleryModal.hidden = true;
        this.galleryModal.setAttribute('aria-hidden', 'true');
        if (this.taxModal?.hidden !== false) document.body.classList.remove('fp-modal-open');
        this.galleryModalTrigger?.focus();
        this.modalItems = [];
        if (this.galleryModalThumbs) this.galleryModalThumbs.innerHTML = '';
      }

      openTaxModal() {
        this.taxModal.hidden = false;
        document.body.classList.add('fp-modal-open');
        this.taxCheckbox?.focus();
      }

      closeTaxModal() {
        if (!this.taxModal) return;
        this.taxModal.hidden = true;
        document.body.classList.remove('fp-modal-open');
        if (!this.taxConfirmed) {
          this.showMessage('Bitte bestätigen Sie die Steuerbefreiung, um fortzufahren.', 'warning');
        }
      }

      async completePurchase(action) {
        const config = this.currentConfiguration();
        // Reconcile gifts with the latest configuration; only eligible +
        // selected cards are added, each as a separate free line item.
        this.syncGifts(config);
        const items = [...config.items];
        this.activeGifts.forEach((card) => {
          this.pushItem(items, card.dataset.variantId, 1, card.dataset.title, this.t('roleGift', 'Gratiszugabe'));
        });

        this.setLoading(true);
        const properties = {
          _Bundle: 'Venus D',
          _Configuration: config.title,
          ...(this.taxModal ? { _MwSt: this.taxConfirmed ? 'Ja' : 'Nein' } : {}),
        };
        if (this.dataset.preorder === 'true') properties['_Pre-order'] = 'Ja';

        try {
          const root = window.Shopify?.routes?.root || '/';
          const cartDrawer = document.querySelector('cart-drawer');
          const useDrawer = cartDrawer && cartDrawer.dataset.cartType === 'drawer';
          const sectionsPayload = useDrawer && typeof cartDrawer.getSectionsToRender === 'function'
            ? cartDrawer.getSectionsToRender().map((section) => section.id)
            : [];
          const bundleId = Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
          const requestBody = {
            items: items.map(({ id, quantity, role }) => ({
              id: Number(id),
              quantity,
              properties: {
                ...properties,
                _Bundle_ID: bundleId,
              },
            })),
          };
          if (sectionsPayload.length) {
            requestBody.sections = sectionsPayload;
            requestBody.sections_url = window.location.pathname;
          }
          const response = await fetch(`${root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(requestBody),
          });
          if (!response.ok) {
            const errBody = await response.json().catch(() => ({}));
            throw new Error(errBody.description || errBody.message || this.t('errorAddToCart', 'Die Konfiguration konnte nicht in den Warenkorb gelegt werden.'));
          }
          const parsed = await response.json();
          if (action === 'buy') {
            window.location.assign(`${root}checkout`);
          } else if (useDrawer && parsed.sections) {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-purchase', cartData: parsed });
            const isDrawerOpen = document.body.classList.contains('page-overlay-cart-on');
            cartDrawer.renderContents(parsed, !isDrawerOpen);
            this.setLoading(false);
          } else {
            window.location.assign(`${root}cart`);
          }
        } catch (error) {
          this.showMessage(error.message, 'error');
          this.setLoading(false);
        }
      }

      setLoading(loading) {
        [...new Set([...this.addButtons, ...this.buyButtons])].forEach((button) => {
          if (!button) return;
          button.disabled = loading;
          button.setAttribute('aria-busy', String(loading));
        });
        const spinner = this.querySelector('.fp-button-spinner');
        if (spinner) spinner.hidden = !loading;
      }

      updateUrl() {
        if (!window.history?.replaceState) return;
        const url = new URL(window.location.href);
        url.searchParams.set('bundle_mode', this.mode);
        if (this.mode === 'recommended') {
          const recommendation = this.selected('.is-selected', this.recommendations);
          if (recommendation?.dataset.blockId) url.searchParams.set('bundle_rec', recommendation.dataset.blockId);
        } else {
          url.searchParams.set('bundle_main', String(this.mainQuantity));
          url.searchParams.set('bundle_battery', String(this.batteryQuantity));
          const solar = this.selected('.is-selected', this.customSolarOptions);
          if (solar?.dataset.panelCount) url.searchParams.set('bundle_solar', solar.dataset.panelCount);
        }
        window.history.replaceState({}, '', url);
      }

      restoreUrlState() {
        const params = new URLSearchParams(window.location.search);
        const recId = params.get('bundle_rec');
        const rec = this.recommendations.find((item) => item.dataset.blockId === recId);
        if (rec) rec.click();
        const main = Number(params.get('bundle_main'));
        if (Number.isFinite(main) && main > 0) {
          this.mainQuantity = main;
          const output = this.querySelector('[data-main-qty-output]');
          if (output) output.textContent = String(this.mainQuantity);
        }
        const battery = Number(params.get('bundle_battery'));
        if (Number.isFinite(battery) && battery > 0) {
          this.batteryQuantity = Math.min(this.number(this.battery?.dataset.max, 5), battery);
          const output = this.querySelector('[data-qty-output]');
          if (output) output.textContent = String(this.batteryQuantity);
          this.updateBatteryCard();
        }
        const panels = params.get('bundle_solar');
        const solar = this.customSolarOptions.find((item) => item.dataset.panelCount === panels);
        if (solar) {
          this.customSolarOptions.forEach((item) => item.classList.toggle('is-selected', item === solar));
        }
      }

      formatMoney(cents) {
        if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents, this.dataset.moneyFormat);
        try {
          return new Intl.NumberFormat(document.documentElement.lang || 'de-DE', { style: 'currency', currency: this.dataset.currency || 'EUR' }).format(cents / 100);
        } catch (error) {
          return `${(cents / 100).toFixed(2)} €`;
        }
      }

      formatPower(value, capacity = false) {
        if (capacity && value >= 1000) return `${new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 }).format(value / 1000)} kWh`;
        return `${new Intl.NumberFormat('de-DE').format(value)} W${capacity ? 'h' : ''}`;
      }

      number(value, fallback = 0) {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : fallback;
      }

      parseI18n() {
        const script = this.querySelector('[data-purchase-i18n]');
        if (!script) return {};
        try {
          return JSON.parse(script.textContent.trim()) || {};
        } catch (error) {
          return {};
        }
      }

      parseGalleryMaps() {
        const script = this.querySelector('[data-gallery-maps]');
        const empty = { battery: null, solar: null };
        if (!script) return empty;
        try {
          const parsed = JSON.parse(script.textContent.trim());
          return {
            battery: Array.isArray(parsed.battery) ? parsed.battery : null,
            solar: Array.isArray(parsed.solar) ? parsed.solar : null,
          };
        } catch (error) {
          return empty;
        }
      }

      lookupMetaImage(map, key, value) {
        if (!Array.isArray(map) || value === null || value === undefined) return null;
        const target = String(value);
        const entry = map.find((item) => item && String(item[key]) === target);
        return entry && entry.image ? entry.image : null;
      }

      setText(selector, value, hidden = false) {
        const node = this.querySelector(selector);
        if (!node) return;
        node.textContent = value || '';
        node.hidden = hidden;
      }

      clearMessage() {
        this.querySelectorAll('[data-purchase-notice]').forEach((notice) => {
          notice.hidden = true;
          delete notice.dataset.type;
        });
      }

      showMessage(message, type) {
        const notices = this.querySelectorAll('[data-purchase-notice]');
        if (!notices.length) return;
        notices.forEach((notice) => {
          const textEl = notice.querySelector('[data-purchase-message-text]');
          if (textEl) textEl.textContent = message;
          notice.dataset.type = type || 'error';
          notice.hidden = false;
        });
      }
    },
  );
}
